/**
 * Vertical cross-section panel (SPEC §5.6).
 *
 * For lee-side decisions this beats the plan view: the separation bubble and
 * the wave structure sit above one another and you can see which one you would
 * be flying into. Streamlines are integrated in the section plane from the
 * in-plane velocity components, so they are true streamlines of the projected
 * flow rather than decoration.
 */

import { useEffect, useMemo, useRef } from 'react';
import { analyseGlide, type CrossSection, type GlideAnalysis } from '../physics/crossSection';
import { inferno, rgbToCss } from '../render/colormaps';
import { useStore } from '../state/store';

interface Props {
  section: CrossSection | null;
  onClose: () => void;
}

export function CrossSectionPanel({ section, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const heightBand = useStore((s) => s.heightBand);
  const glide = useMemo(() => (section ? analyseGlide(section) : null), [section]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !section) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw(ctx, rect.width, rect.height, section, heightBand);
  }, [section, heightBand]);

  if (!section || !glide) return null;

  return (
    <div className="cross-section">
      <div className="cs-head">
        <h3>
          Cross-section · {(section.totalLength / 1000).toFixed(1)} km
          <span className="cs-sub">
            looking along {bearingLabel(section.dir)} — left is the start point
          </span>
        </h3>
        <button onClick={onClose} aria-label="Close cross-section">
          ×
        </button>
      </div>

      <GlideReadout glide={glide} />

      <canvas ref={canvasRef} className="cs-canvas" />
      <div className="cs-legend">
        <span>
          <i className="cs-key streamline" /> in-plane streamlines
        </span>
        <span>
          <i className="cs-key rotor" /> separation at surface
        </span>
        <span>
          <i className="cs-key band" /> {heightBand} m band
        </span>
      </div>
    </div>
  );
}

/** Format a glide ratio the way pilots quote it: 1:6.3. */
function ratioLabel(r: number): string {
  if (!Number.isFinite(r)) return '—';
  return `1:${r.toFixed(1)}`;
}

/**
 * The numbers a pilot wants before committing to a glide: how much height the
 * line costs, the ratio that gets there, and — the one that actually bites —
 * the ratio that also clears whatever stands in between.
 */
function GlideReadout({ glide }: { glide: GlideAnalysis }) {
  const reachable = Number.isFinite(glide.ratio);
  const blocked = !Number.isFinite(glide.requiredRatio);
  const tight = reachable && !blocked && glide.requiredRatio > glide.ratio * 1.08;

  return (
    <div className={`cs-glide${!reachable || blocked ? ' warn' : ''}`}>
      {!reachable ? (
        <span className="cs-glide-main">
          End point is {Math.abs(glide.drop).toFixed(0)} m <em>higher</em> than the start
          — no glide reaches it
        </span>
      ) : (
        <>
          <span className="cs-glide-main">
            Glide <strong>{ratioLabel(glide.ratio)}</strong>
          </span>
          <span className="cs-glide-detail">
            {glide.drop.toFixed(0)} m down over {(glide.distance / 1000).toFixed(2)} km
          </span>
          {blocked ? (
            <span className="cs-glide-detail warn">
              terrain ahead stands above the start — not clearable in a straight line
            </span>
          ) : tight ? (
            <span className="cs-glide-detail warn">
              needs <strong>{ratioLabel(glide.requiredRatio)}</strong> to clear the ground
              at {(glide.minClearanceAt / 1000).toFixed(2)} km
            </span>
          ) : (
            <span className="cs-glide-detail">
              clears by {glide.minClearance.toFixed(0)} m at{' '}
              {(glide.minClearanceAt / 1000).toFixed(2)} km
            </span>
          )}
        </>
      )}
    </div>
  );
}

const PAD = { left: 46, right: 12, top: 12, bottom: 26 };

function draw(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  cs: CrossSection,
  heightBand: number
): void {
  ctx.clearRect(0, 0, w, h);

  const plotW = w - PAD.left - PAD.right;
  const plotH = h - PAD.top - PAD.bottom;

  let minGround = Infinity;
  let maxGround = -Infinity;
  for (let i = 0; i < cs.ns; i++) {
    minGround = Math.min(minGround, cs.ground[i]);
    maxGround = Math.max(maxGround, cs.ground[i]);
  }
  // Show the ground plus enough air above the highest point to hold the
  // structure that matters, rather than the full solver domain.
  const topOfPlot = maxGround + Math.max(700, (maxGround - minGround) * 1.4);
  const bottom = minGround - 40;
  const yFor = (elev: number) =>
    PAD.top + plotH * (1 - (elev - bottom) / (topOfPlot - bottom));
  const xFor = (dist: number) => PAD.left + (dist / cs.totalLength) * plotW;

  // --- speed field --------------------------------------------------------
  let maxSpeed = 1;
  for (let i = 0; i < cs.speed.length; i++) maxSpeed = Math.max(maxSpeed, cs.speed[i]);

  const cellW = plotW / (cs.ns - 1);
  for (let s = 0; s < cs.ns; s++) {
    const gx = xFor(cs.distance[s]);
    for (let k = 0; k < cs.nz; k++) {
      const elev = cs.ground[s] + cs.zAGL[k];
      if (elev > topOfPlot) break;
      const yTop = yFor(k < cs.nz - 1 ? cs.ground[s] + cs.zAGL[k + 1] : elev + 60);
      const yBot = yFor(k > 0 ? cs.ground[s] + cs.zAGL[k - 1] : cs.ground[s]);
      const t = Math.min(1, cs.speed[k * cs.ns + s] / maxSpeed);
      // Kept translucent: the panel's job is to show structure, and a solid
      // ramp buries the streamlines and the terrain silhouette under it.
      ctx.fillStyle = rgbToCss(inferno(t), 0.34);
      ctx.fillRect(gx - cellW / 2, yTop, cellW + 1, Math.max(1, yBot - yTop));
    }
  }

  // --- streamlines --------------------------------------------------------
  // Sparse on purpose. Densely packed lines fill the panel and bury the things
  // you actually came here to read — the separation bubble, the wave structure
  // and the terrain silhouette. Enough to show the shape of the flow, no more.
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 1.0;
  const seeds = 10;
  for (let i = 0; i <= seeds; i++) {
    const zStart = (i / seeds) * (topOfPlot - minGround) * 0.9;
    traceStreamline(ctx, cs, 0, zStart, xFor, yFor, topOfPlot, 1);
  }
  // Seed backwards from the right edge too, so lee-side recirculation shows.
  for (let i = 0; i <= seeds; i += 3) {
    const zStart = (i / seeds) * (topOfPlot - minGround) * 0.9;
    traceStreamline(ctx, cs, cs.totalLength, zStart, xFor, yFor, topOfPlot, -1);
  }

  // --- separation at the surface ------------------------------------------
  for (let s = 0; s < cs.ns; s++) {
    const strength = cs.separation[s];
    if (strength < 0.15) continue;
    const gx = xFor(cs.distance[s]);
    const gTop = yFor(cs.ground[s] + 260 * strength);
    const gBot = yFor(cs.ground[s]);
    ctx.fillStyle = `rgba(237,128,51,${Math.min(0.45, strength * 0.55)})`;
    ctx.fillRect(gx - cellW / 2, gTop, cellW + 1, gBot - gTop);
  }

  // --- terrain ------------------------------------------------------------
  ctx.beginPath();
  ctx.moveTo(xFor(0), yFor(cs.ground[0]));
  for (let s = 1; s < cs.ns; s++) ctx.lineTo(xFor(cs.distance[s]), yFor(cs.ground[s]));
  ctx.lineTo(xFor(cs.totalLength), h - PAD.bottom);
  ctx.lineTo(xFor(0), h - PAD.bottom);
  ctx.closePath();
  ctx.fillStyle = '#171b22';
  ctx.fill();
  ctx.strokeStyle = '#59606e';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // --- displayed height band ----------------------------------------------
  ctx.setLineDash([5, 4]);
  ctx.strokeStyle = 'rgba(122,196,255,0.85)';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (let s = 0; s < cs.ns; s++) {
    const y = yFor(cs.ground[s] + heightBand);
    if (s === 0) ctx.moveTo(xFor(cs.distance[s]), y);
    else ctx.lineTo(xFor(cs.distance[s]), y);
  }
  ctx.stroke();
  ctx.setLineDash([]);

  // --- axes ---------------------------------------------------------------
  ctx.fillStyle = 'rgba(220,226,236,0.75)';
  ctx.font = '10px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  const steps = 4;
  for (let i = 0; i <= steps; i++) {
    const elev = bottom + ((topOfPlot - bottom) * i) / steps;
    const y = yFor(elev);
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath();
    ctx.moveTo(PAD.left, y);
    ctx.lineTo(w - PAD.right, y);
    ctx.stroke();
    ctx.fillText(`${Math.round(elev)} m`, PAD.left - 6, y);
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (let i = 0; i <= 4; i++) {
    const d = (cs.totalLength * i) / 4;
    ctx.fillText(`${(d / 1000).toFixed(1)} km`, xFor(d), h - PAD.bottom + 6);
  }
}

/**
 * Integrate a streamline through the in-plane velocity field with RK2.
 * `direction` is +1 downstream or −1 upstream.
 */
function traceStreamline(
  ctx: CanvasRenderingContext2D,
  cs: CrossSection,
  startDist: number,
  startAGL: number,
  xFor: (d: number) => number,
  yFor: (e: number) => number,
  topOfPlot: number,
  direction: number
): void {
  const maxSteps = 420;
  const dt = (cs.totalLength / cs.ns / 12) * direction;

  let dist = startDist;
  let agl = startAGL;
  let started = false;
  ctx.beginPath();

  for (let step = 0; step < maxSteps; step++) {
    const v1 = sampleSection(cs, dist, agl);
    if (!v1) break;
    const midDist = dist + v1.along * dt * 0.5;
    const midAgl = agl + v1.w * dt * 0.5;
    const v2 = sampleSection(cs, midDist, midAgl) ?? v1;

    dist += v2.along * dt;
    agl += v2.w * dt;

    const ground = sampleGround(cs, dist);
    if (ground === null) break;
    const elev = ground + agl;
    if (agl < 12 || elev > topOfPlot) break;
    if (dist < 0 || dist > cs.totalLength) break;

    const px = xFor(dist);
    const py = yFor(elev);
    if (!started) {
      ctx.moveTo(px, py);
      started = true;
    } else {
      ctx.lineTo(px, py);
    }
  }
  if (started) ctx.stroke();
}

function sampleGround(cs: CrossSection, dist: number): number | null {
  if (dist < 0 || dist > cs.totalLength) return null;
  const f = (dist / cs.totalLength) * (cs.ns - 1);
  const i0 = Math.max(0, Math.min(cs.ns - 1, Math.floor(f)));
  const i1 = Math.min(cs.ns - 1, i0 + 1);
  const t = f - i0;
  return cs.ground[i0] * (1 - t) + cs.ground[i1] * t;
}

function sampleSection(
  cs: CrossSection,
  dist: number,
  agl: number
): { along: number; w: number } | null {
  if (dist < 0 || dist > cs.totalLength) return null;
  const f = (dist / cs.totalLength) * (cs.ns - 1);
  const i0 = Math.max(0, Math.min(cs.ns - 1, Math.floor(f)));
  const i1 = Math.min(cs.ns - 1, i0 + 1);
  const tx = f - i0;

  const z = cs.zAGL;
  if (agl <= z[0]) return pick(cs, i0, i1, tx, 0, 0, 0);
  if (agl >= z[cs.nz - 1]) return pick(cs, i0, i1, tx, cs.nz - 1, cs.nz - 1, 0);
  let k = 0;
  while (k < cs.nz - 2 && z[k + 1] < agl) k++;
  const tz = (agl - z[k]) / (z[k + 1] - z[k]);
  return pick(cs, i0, i1, tx, k, k + 1, tz);
}

function pick(
  cs: CrossSection,
  i0: number,
  i1: number,
  tx: number,
  k0: number,
  k1: number,
  tz: number
): { along: number; w: number } {
  const at = (arr: Float32Array, k: number, i: number) => arr[k * cs.ns + i];
  const lerp = (arr: Float32Array) =>
    (at(arr, k0, i0) * (1 - tx) + at(arr, k0, i1) * tx) * (1 - tz) +
    (at(arr, k1, i0) * (1 - tx) + at(arr, k1, i1) * tx) * tz;
  return { along: lerp(cs.along), w: lerp(cs.w) };
}

function bearingLabel(dir: [number, number]): string {
  const deg = (Math.atan2(dir[0], dir[1]) * 180) / Math.PI;
  const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return names[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
}
